import { Link } from "react-router-dom";
import { Card, Spinner, ErrorBox, timeAgo } from "./ui";
import type { ActivityEvent, ActivityKind } from "@shared/types";
import { candidatePath } from "@shared/share-links";
import { useT } from "../i18n";

const ICON: Record<ActivityKind, string> = {
  candidacy: "⚔",
  candidacy_update: "✏️",
  ama_question: "❓",
  ama_answer: "💬",
  article: "📰",
  poll: "🗳",
};

/** Where a row links: poll events → the poll itself (hub when the id is
 * missing); the rest → the candidate's canonical profile (falls back to the
 * deep candidacy URL when anonymous). */
function hrefFor(e: ActivityEvent): string {
  if (e.kind === "poll") return e.pollId ? `/polls/${e.pollId}` : "/polls";
  if (e.username) return candidatePath(e.username);
  return `/c/${e.countryId}/race/${e.office}/${e.candidacyId}`;
}

// "Latest in {nation}" wire — recent events, each clickable to its source.
export function ActivityFeed({
  events,
  loading,
  error,
}: {
  events: ActivityEvent[] | null;
  loading: boolean;
  error: string | null;
}) {
  const t = useT();
  if (loading) return <Spinner label={t("activity.loading")} />;
  if (error) return <ErrorBox msg={error} />;
  if (!events || events.length === 0) {
    return <Card><p className="text-muted text-sm">{t("activity.empty")}</p></Card>;
  }

  return (
    <Card className="divide-y divide-border p-0">
      {events.map((e, i) => (
        <Link
          key={`${e.kind}-${e.candidacyId ?? e.pollId}-${i}`}
          to={hrefFor(e)}
          className="flex items-start gap-3 px-4 py-3 hover:bg-panel2 transition"
        >
          <span className="text-lg leading-none mt-0.5">{ICON[e.kind]}</span>
          <span className="min-w-0 flex-1">
            <span className="text-sm">{label(e, t)}</span>
            {e.detail && <span className="block text-xs text-muted truncate">{e.detail}</span>}
          </span>
          <span className="text-[11px] text-muted shrink-0">{timeAgo(e.at, t)}</span>
        </Link>
      ))}
    </Card>
  );
}

function label(e: ActivityEvent, t: ReturnType<typeof useT>): React.ReactNode {
  const office = e.office === "president" ? t("office.president") : t("office.congress");
  if (e.kind === "candidacy")
    return (
      <>
        {t("activity.newCandidacy")} <b>{e.username}</b> — <span className="capitalize">{office}</span>
      </>
    );
  if (e.kind === "candidacy_update")
    return (
      <>
        <b>{e.username}</b> {t("activity.updatedCampaign")}
      </>
    );
  if (e.kind === "ama_question")
    return (
      <>
        {t("activity.newQuestionFor")} <b>{e.username}</b>
      </>
    );
  if (e.kind === "ama_answer")
    return (
      <>
        <b>{e.username}</b> {t("activity.answered")}
      </>
    );
  if (e.kind === "poll") return <>{t("activity.newPoll")}</>;
  return (
    <>
      <b>{e.username}</b> {t("activity.postedUpdate")}
    </>
  );
}
